import { useMemo, useState } from 'react';
import { RankingEvolutionView } from '../components/rankings/RankingEvolutionView';
import type { RankingTimelineData, RankingTimelineRound } from '../types';

const NAMES = [
  'Rafael', 'Maria', 'Yago', 'Juliana', 'Bruno', 'Camila', 'Diego',
  'Fernanda', 'Gustavo', 'Helena', 'Igor', 'Larissa', 'Marcelo', 'Natália',
];

const ENTRANT_INDEX = 13; // Natália — joins mid-season (round 4)
const NEGATIVE_INDEX = 12; // Marcelo — WO losses pull him below zero
const ROUNDS = 8;

/**
 * Deterministic fixture exercising the tricky cases: crossings, a mid-season
 * entrant, a negative-points player (floor bar), and top-10 enter/exit.
 */
function buildFixture(): RankingTimelineData {
  const players = NAMES.map((name, i) => ({ id: `p${i + 1}`, name, avatarUrl: null }));
  const cumulative = new Array(NAMES.length).fill(0);
  const rounds: RankingTimelineRound[] = [];

  for (let r = 1; r <= ROUNDS; r++) {
    for (let i = 0; i < NAMES.length; i++) {
      if (i === ENTRANT_INDEX && r < 4) continue; // not in the season yet
      let delta: number;
      if (i === NEGATIVE_INDEX) {
        delta = r % 2 === 0 ? -3 : 1; // net negative over the season
      } else if (i === ENTRANT_INDEX) {
        delta = 12; // storms up the board after joining
      } else {
        delta = ((i * 7 + r * 13) % 9) + 2; // 2..10, produces crossings
      }
      cumulative[i] += delta;
    }

    const entered = players
      .map((p, i) => ({ i, p }))
      .filter(({ i }) => !(i === ENTRANT_INDEX && r < 4));

    const standings = entered
      .map(({ i, p }) => ({ playerId: p.id, points: cumulative[i], name: p.name }))
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        const cmp = a.name.localeCompare(b.name, 'pt-BR');
        if (cmp !== 0) return cmp;
        return a.playerId < b.playerId ? -1 : 1;
      })
      .map((s, idx) => ({ playerId: s.playerId, points: s.points, position: idx + 1, rank: idx + 1 }));

    rounds.push({ roundNumber: r, standings });
  }

  return { players, rounds };
}

/**
 * Public, unauthenticated playground for iterating on the bar-chart race without
 * a real ranking. Opens the same `RankingEvolutionView` used in-app, fed a
 * synthetic timeline. Sharing is disabled (no backend ranking behind it).
 */
export function BarRacePlaygroundPage() {
  const [open, setOpen] = useState(true);
  const timeline = useMemo(buildFixture, []);

  return (
    <div className="min-h-screen bg-deep-blue text-white flex flex-col items-center justify-center gap-4 p-6">
      <h1 className="text-lg font-bold">Bar Race Playground</h1>
      <p className="text-white/50 text-sm text-center max-w-sm">
        {timeline.players.length} jogadores · {timeline.rounds.length} rodadas · inclui entrante
        no meio da temporada, jogador com pontuação negativa e trocas no top 10.
      </p>
      <button
        onClick={() => setOpen(true)}
        className="px-5 py-2.5 rounded-full bg-sunny text-deep-blue font-semibold"
      >
        Abrir evolução
      </button>

      {open && (
        <RankingEvolutionView
          rankingId="playground"
          rankingName="Panela Beach"
          rankingImageUrl={null}
          initialData={timeline}
          canShare={false}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
